<template>
    <transition>
    <div class="container-fluid page-content">
        <!-- Modern Page Header -->
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
            <div>
                <h4 class="page-title text-slate-800 fw-bold mb-1">Brand</h4>
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb mb-0">
                        <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                        <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Product Settings</a></li>
                        <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Brand List</li>
                    </ol>
                </nav>
            </div>
            <div>
                <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal()" v-if="permission['brand-create']">
                    <i class="fas fa-plus"></i> Add New Brand
                </button>
            </div>
        </div>

        <!-- Modern Filter Dashboard Bar -->
        <div class="card border-0 shadow-sm filter-card">
            <div class="card-body p-3">
                <div class="row align-items-center g-2">
                    <div class="col-md-6 col-sm-6 d-flex align-items-center gap-3">
                        <div class="d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select-custom length-select" v-model="tableData.length" @change="getDataItems()">
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option :value="pagination.total">All</option>
                            </select>
                            <span class="text-muted text-nowrap">entries</span>
                        </div>
                    </div>
                    <div class="col-md-6 col-sm-6 d-flex justify-content-sm-end">
                        <div class="position-relative search-container">
                            <i class="fas fa-search search-icon text-muted"></i>
                            <input type="text" class="form-control-custom search-input" v-model="tableData.search" placeholder="Search brands..." @input="getDataItems()">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modern Table List Card -->
        <div class="card border-0 shadow-sm table-card">
            <div class="card-body p-0">
                <div class="table-responsive">
                    <table class="table table-premium mb-0" v-if="!loading && items.length > 0">
                        <thead>
                            <tr>
                                <th class="text-center" style="width: 5%;">SL</th>
                                <th class="text-start" @click="sortBy('name', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 18%; cursor: pointer;">
                                    Brand Name
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'name' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'name' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-center" style="width: 10%;">Image</th>
                                <th class="text-start" style="width: 17%;">Description</th>
                                <th class="text-start" style="width: 15%;">Website</th>
                                <th class="text-start" style="width: 13%;">Company</th>
                                <th class="text-center" style="width: 10%;">Status</th>
                                <th class="text-center" style="width: 7%;">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, i) in items" :key="i">
                                <td class="text-center text-muted fw-semibold">
                                    {{ (pagination && pagination.currentPage ? (pagination.currentPage - 1) * tableData.length : 0) + i + 1 }}
                                </td>
                                <td class="text-start">
                                    <span class="fw-bold text-slate-800">{{ item.name }}</span>
                                </td>
                                <td class="text-center">
                                    <div class="image-avatar-container d-inline-block">
                                        <img :src="item.logo" class="image-avatar" v-if="item.logo" alt="Brand Logo">
                                        <div class="image-avatar-placeholder" v-else>
                                            <i class="fas fa-tag text-muted"></i>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-start text-muted">{{ item.description || '—' }}</td>
                                <td class="text-start text-muted">{{ item.website || '—' }}</td>
                                <td class="text-start text-muted">{{ item.company_name || '—' }}</td>
                                <td class="text-center">
                                    <span v-if="item.status==1" class="badge-role badge-role-active">Active</span>
                                    <span v-if="item.status==0" class="badge-role badge-role-inactive">Inactive</span>
                                </td>
                                <td class="text-center">
                                    <div class="dropdown">
                                        <button class="btn btn-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="fas fa-ellipsis-v"></i>
                                        </button>
                                        <ul class="dropdown-menu dropdown-menu-end border-0 shadow-lg dropdown-premium">
                                            <li>
                                                <a class="dropdown-item text-primary" href="javascript:void(0);" @click="edit(item)" v-if="permission['brand-edit']">
                                                    <i class="fas fa-edit me-2"></i>Edit Brand
                                                </a>
                                            </li>
                                            <li><hr class="dropdown-divider"></li>
                                            <li>
                                                <a class="dropdown-item text-danger" href="javascript:void(0);" @click="deleteItem(item)" v-if="permission['brand-delete']">
                                                    <i class="fas fa-trash-alt me-2"></i>Remove
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Empty State -->
                    <div v-if="!loading && items.length === 0" class="text-center py-5">
                        <img src="../../assets/image/loading.gif" class="mb-3 opacity-25" style="width: 80px; filter: grayscale(1);" />
                        <h5 class="text-muted fw-semibold">No Brands Found</h5>
                        <p class="text-muted mb-0 small">Try adjusting your filters or search keywords.</p>
                    </div>

                    <!-- Loading State -->
                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-premium mb-3"></div>
                        <h5 class="text-muted fw-semibold">Loading Brands</h5>
                        <p class="text-muted mb-0 small">Please wait a moment...</p>
                    </div>
                </div>

                <!-- Modern Pagination Footer -->
                <div class="card-footer border-0 bg-white py-3 px-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3" v-if="pagination && items.length > 0">
                    <div class="text-muted small">
                        Showing <span class="fw-semibold text-slate-800">{{ pagination.from }}</span> to <span class="fw-semibold text-slate-800">{{ pagination.to }}</span> of <span class="fw-semibold text-slate-800">{{ pagination.total }}</span> entries
                    </div>
                    <Pagination
                        :pagination="pagination"
                        :language="lang"
                        @onChangePage="setPage" >
                    </Pagination>
                </div>
            </div>
        </div>

        <!-- Modern Add/Edit Modal -->
        <Modal @close="toggleModal()" :modalActive="modalActive">
            <div class="modal-content border-0 shadow-lg modal-premium">
                <div class="modal-header">
                    <h4 class="fw-bold m-0">{{ editMode ? 'Update Brand' : 'Create New Brand' }}</h4>
                    <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                </div>
                <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                    <div class="modal-body py-4">
                        <div class="row g-4">
                            <div class="col-md-8">
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label for="name" class="form-label-custom text-slate-700 fw-semibold mb-2">Brand Name *</label>
                                            <input type="text" class="form-control-custom" :class="{'is-invalid': errors.name}"
                                                @keypress="onkeyPress('name')" v-model="form.name" id="name"
                                                placeholder="Enter brand name" autocomplete="off">
                                            <div class="invalid-feedback" v-if="errors.name">
                                                {{errors.name[0]}}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label for="company_id" class="form-label-custom text-slate-700 fw-semibold mb-2">Company</label>
                                            <select class="form-select-custom" :class="{'is-invalid': errors.company_id}" v-model="form.company_id"
                                                @change="onkeyPress('company_id')" id="company_id">
                                                <option value="0">Select Company</option>
                                                <option v-for="(company, index) in companies" :value="company.id"
                                                    :key="index">{{ company.name }}</option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.company_id">
                                                {{errors.company_id[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group mt-3">
                                    <label for="description" class="form-label-custom text-slate-700 fw-semibold mb-2">Description</label>
                                    <input type="text" class="form-control-custom" :class="{'is-invalid': errors.description}"
                                        @keypress="onkeyPress('description')" v-model="form.description"
                                        id="description" placeholder="Description Here!">
                                    <div class="invalid-feedback" v-if="errors.description">
                                        {{errors.description[0]}}
                                    </div>
                                </div>

                                <div class="form-group mt-3">
                                    <label for="website" class="form-label-custom text-slate-700 fw-semibold mb-2">Website</label>
                                    <input type="text" class="form-control-custom" :class="{'is-invalid': errors.website}"
                                        @keypress="onkeyPress('website')" v-model="form.website" id="website"
                                        placeholder="Website Link Here">
                                    <div class="invalid-feedback" v-if="errors.website">
                                        {{errors.website[0]}}
                                    </div>
                                </div>

                                <div class="row g-3 mt-1">
                                    <div class="col-md-6 d-flex align-items-end">
                                        <div class="form-check-custom">
                                            <input type="checkbox" class="form-check-input-custom" id="is_featured"
                                                v-model="form.is_featured" true-value="1" false-value="0"
                                                @change="onkeyPress('is_featured')">
                                            <label class="form-check-label-custom" for="is_featured">Featured Brand</label>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label for="bstatus" class="form-label-custom text-slate-700 fw-semibold mb-2">Status</label>
                                            <select class="form-select-custom" v-model="form.status"
                                                @change="onkeyPress('status')" id="bstatus">
                                                <option value="1">Active</option>
                                                <option value="0">Inactive</option>
                                            </select>
                                            <div class="invalid-feedback" v-if="errors.status">
                                                {{errors.status[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row g-3 mt-2">
                                    <div class="col-md-6">
                                        <div class="form-group mb-0">
                                            <label class="form-label-custom text-slate-700 fw-semibold mb-2">Brand Logo (128×128) *</label>
                                            <div class="file-upload-wrapper">
                                                <button type="button" class="btn btn-outline-file-upload btn-sm">
                                                    <i class="fas fa-camera me-2"></i>Choose Logo
                                                </button>
                                                <input type="file" class="file-upload-input" name="..." @change="onImageChange" />
                                            </div>
                                            <div class="invalid-feedback d-block" v-if="errors.logo">
                                                {{errors.logo[0]}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4 text-center">
                                <label class="form-label-custom text-slate-700 fw-semibold mb-2 d-block text-center">Logo Preview</label>
                                <div class="modal-image-preview-container d-inline-block shadow-sm">
                                    <img :src="imagePreview" v-if="form.logo && imagePreview" class="modal-image-preview" alt="New Upload Preview">
                                    <img :src="form.logo" v-else-if="editMode && form.logo" class="modal-image-preview" alt="Current Logo">
                                    <div class="modal-image-placeholder" v-else>
                                        <i class="fas fa-image fa-3x text-muted opacity-25"></i>
                                        <span class="d-block mt-2 small text-muted">No Logo Chosen</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer border-0 pt-0">
                        <button type="button" class="btn btn-outline-premium me-2" @click="toggleModal()">Cancel</button>
                        <button type="submit" class="btn btn-premium" :disabled="disabled">
                            <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ editMode ? 'Update Brand' : 'Create Brand' }}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    </div>
    </transition>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { useProductStore } from '@/stores/product';
import { useRequisitionProductStore } from '@/stores/requisitionProduct';


import Modal from "./../helper/Modal";
import Datatable from '@/components/Datatable.vue';
import Pagination from '@/components/Pagination.vue';
import { ref, onMounted } from "vue";
import Form from 'vform'
import axios from 'axios';
export default {
    name: 'Brand',
    components: {
        Modal,
        Datatable,
        Pagination,
    },
    props:{
        language: {
          type: Object,
          default: () => {
            return {
              lengthMenu: null,
              info: null,
              zeroRecords: null, 
              search: null
            }
          },
        },
    },
    data() {
        return {
            loading: true,
            isSubmit: false,
            showModal: false,
            editMode: false,
            disabled: false,
            modalActive: false,
            errors: {},
            imagePreview: '',
            btn: 'Create',
            items: [],
            companies: [],
            form: new Form({
                id: '',
                name: '',
                description: '',
                website: '',
                logo: '',
                company_id: 0,
                is_featured: 0,
                status: 0,
            }),
            columns: [ 
                {
                    label: 'Brand Name',
                    name: 'name',           
                    width: '15%'
                },   
                {
                    label: 'Image',
                    name: 'logo',
                    isSearch: false, 
                    width: '15%'
                },
                {
                    label: 'Description',
                    name: 'description',
                    width: '20%'
                },
                {
                    label: 'Website',
                    name: 'website',
                    width: '15%'
                },
                {
                    label: 'Company',
                    name: 'company_name',
                    width: '15%'
                },
                {
                    label: 'Status',
                    name: 'status',
                    width: '10%'
                },
                {
                    label: 'Actions',            
                    name: '',
                    isSearch: false, 
                    isAction: true,
                    width: '5%',

                }
            ],  
            tableData: {
                draw: 0,
                length: 10,
                search: '',
                column: 0,
                dir: 'desc',
                sortKey: 'name', 
            }, 
            lang: {
                lengthMenu: this.$props.language.lengthMenu ? this.$props.language.lengthMenu : 'Show_MENU_entries',
                info: this.$props.language.info ? this.$props.language.info : 'Showing_FROM_to_TO_of_TOTAL_entries',
                zeroRecords: this.$props.language.zeroRecords ? this.$props.language.zeroRecords : 'No data available in table.', 
                search: this.$props.language.search ? this.$props.language.search : 'Search'
            },
            pagination: {
                lastPage: '',
                currentPage: '',
                total: '',
                lastPageUrl: '',
                nextPageUrl: '',
                prevPageUrl: '',
                from: '',
                to: '',
                links:[],
            }
        };
    },
    created() {
        this.getDataItems();
        this.fetchCompanyData();
    },
    methods: {
        toggleModal: function () {
            this.modalActive = !this.modalActive;
            if (!this.modalActive) {
                this.editMode = false;
                this.btn = 'Create';
            }
            this.errors = '';
            this.isSubmit = false;
            this.form.reset();
        },
        fetchCompanyData() {
            axios.get(this.apiUrl + '/companies', this.headerjson)
                .then((res) => {
                    this.companies = res.data.data;
                })
                .catch((err) => {
                    this.$toast.error(err.response.data.message);
                });
        },

        edit: function (item) {
            this.btn = 'Update';
            this.editMode = true;
            this.toggleModal();
            this.form.fill(item);
        },
        submitForm: function (e) {

            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();
            formData.append('name', this.form.name);
            formData.append('company_id', this.form.company_id);
            formData.append('description', this.form.description);
            formData.append('website', this.form.website);
            formData.append('is_featured', this.form.is_featured);
            formData.append('status', this.form.status);
            if (this.editMode) {
                formData.append('_method', 'put');
                if (this.imagePreview) {
                    this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
                }
                var postEvent = axios.post(this.apiUrl + '/brands/' + this.form.id, formData, this.headers);
            } else {
                this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
                var postEvent = axios.post(this.apiUrl + '/brands', formData, this.headers);
            }

            postEvent.then(res => {
                this.isSubmit = false;
                this.disabled = false;
                if (res.status == 200) {
                    this.toggleModal(); 
                    this.getDataItems(this.apiUrl+'/brands?page='+this.pagination.currentPage);
                    this.$toast.success(res.data.message);
                } else {
                    this.$toast.error(res.data.message);
                }
            }).catch(err => {
                this.isSubmit = false;
                this.disabled = false;
                this.$toast.error(err.response.data.message);
                if (err.response.status == 422) {
                    this.errors = err.response.data.errors
                }
            });
        },

        onImageChange(e) {
            this.form.logo = e.target.files[0]
            let files = e.target.files || e.dataTransfer.files;
            if (!files.length)
                return;
            this.createImage(files[0]);
        },
        createImage(file) {
            let reader = new FileReader();
            let vm = this;
            reader.onload = (e) => {
                vm.imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        validation: function (...fiels) {
            var obj = new Object();
            var validate = false;
            for (var k in fiels) {     // Loop through the object  
                for (var j in this.form) {
                    if ((j == fiels[k]) && (!this.form[j])) {
                        obj[fiels[k]] = fiels[k].replace("_", " ") + ' field is required';  // Delete obj[key]; 
                        this.errors = { ...this.errors, ...obj };
                    } else {
                        validate = false;
                    }
                }
            }
            // var obj = new Object();
            // obj[fiels] = fiels.replace("_", " ")+' field is required';  
            // this.errors = {...this.errors, ...obj}; 
        },
        onkeyPress: function (field) {
            for (var k in this.errors) {     // Loop through the object
                if (k == field) {      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }
        },
        deleteItem: function (item) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!",
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.value) {
                    axios.delete(this.apiUrl + '/brands/' + item.id, this.headerjson)
                        .then(res => {
                            if (res.status == 200) {
                                this.getDataItems(this.apiUrl+'/brands?page='+this.pagination.currentPage);
                                this.$toast.success(res.data.message);
                            } else {
                                this.$toast.error(res.data.message);
                            }
                        }).catch(err => {
                            this.$toast.error(err.response.data.message);
                        })
                }
            });
        },
        // For Pagination 
        getDataItems(url = this.apiUrl+'/brands') {
            this.tableData.draw++;
            axios.get(url, {params:this.tableData, headers: this.headerparams})
            .then((response) => { 
                this.items = response.data.data;   
                this.configPagination(response.data.meta); 
            })
            .catch(errors => {
                this.$toast.error(errors);
            })
            .finally((fres) => {
                this.loading = false;
            });
        },

        configPagination(data){
            this.pagination.lastPage = data.last_page;
            this.pagination.currentPage = data.current_page;
            this.pagination.total   = data.total ? data.total : 0;
            this.pagination.lastPageUrl = data.last_page_url;
            this.pagination.nextPageUrl = data.next_page_url;
            this.pagination.prevPageUrl = data.prev_page_url;
            this.pagination.from = data.from ? data.from : 0;
            this.pagination.to = data.to ? data.to : 0;  
            this.pagination.links = data.links;
        },

        sortBy(key,sortable) {
            this.tableData.sortKey = key; 
            this.tableData.column = this.getIndex(this.columns, 'name', key);
            this.tableData.dir = sortable;  
            this.getDataItems();
        },
        setPage(data){  
            this.getDataItems(data.url); 
        },
        getIndex(array, key, value) {
            return array.findIndex(i => i[key] == value)
        },

        ...mapActions(useCartStore, ['removeAllCartItems', 'removeCartItem', 'addCartItem']),
    },
    destroyed() { },
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {
        permission() {
            let pname = this.$route.meta.parent_module;
            let module_name = this.$route.meta.module_name;
            let path_name = this.$route.path; 
            let data = '';
            if(this.$route.meta.parent_module){
                data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
            }else{
                data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
            } 
            return data;
        },
    }
}
</script>